import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import getRealTimeWarehouseInventory from "@insite/client-framework/Store/Data/RealTimeInventory/Handlers/GetRealTimeWarehouseInventory";
import translate from "@insite/client-framework/Translate";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconWrapper } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import {
    AvailabilityDto,
    AvailabilityMessageType,
    ProductSettingsModel,
    WarehouseDto,
} from "@MasterBluePrint/Overrides/Types/ApiModels";
import { getCurrentWebsiteCountry } from "@MasterBluePrint/Services/CurrentWebsiteCountryService";
import { 
    getCustomStockSettings, 
    showForeignStockMessageSetting, 
    showVendorStocksSetting
} from "@MasterBluePrint/Store/Context/ContextSelector";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

interface OwnProps {
    productId: string;
    availability?: AvailabilityDto;
    unitOfMeasure: string;
    trackInventory: boolean;
    isProductDetailsPage?: boolean;
    failedToLoadInventory?: boolean;
    extendedStyles?: ProductAvailabilityStyles;
    qtyOnHand: number;
    isDirectShip?: string;
    manufacturerItem?: string;
    manufacturerName?: string;
    isDirectShipFromManufacturer?: string;
    inventoryOutofStock?: string;
    brand?: string;
    sku?: string;
}

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
    showForeignStockCountry: showForeignStockMessageSetting(state)?.showForeignStockInCountry,
    websiteState: state.context.website,
    showVendorStocksSetting: showVendorStocksSetting(state)?.showVendorStocks,
    domesticStockCountries: getCustomStockSettings(state)?.domesticStockCountries
});

const mapDispatchToProps = {
    getRealTimeWarehouseInventory,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductAvailabilityStyles {
    container?: GridContainerProps;
    messageGridItem?: GridItemProps;
    availabilityByWarehouseLinkGridItem?: GridItemProps;
    inventoryMessage?: InjectableCss;
    messageText?: TypographyPresentationProps;
    realTimeText?: TypographyPresentationProps;
    errorWrapper?: InjectableCss;
    errorText?: TypographyPresentationProps;
    availabilityByWarehouseLink?: LinkPresentationProps;
    availabilityByWarehouseModal?: ModalPresentationProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    availabilityByWarehouseTable?: DataTableProps;
    availabilityByWarehouseTableHead?: DataTableHeadProps;
    availabilityByWarehouseTableHeader?: DataTableHeaderProps;
    availabilityByWarehouseTableBody?: DataTableBodyProps;
    availabilityByWarehouseTableRow?: DataTableRowProps;
    availabilityByWarehouseTableCell?: DataTableCellBaseProps;
    labelStyled?: InjectableCss;
    quantityStyled?: InjectableCss;
    requestTime?: InjectableCss;
    messageTextNew?: InjectableCss;
    messageTextContainer?: InjectableCss;
    stockContainerStyle?: InjectableCss;
    outStockContainerStyle?: InjectableCss;
}

export const productAvailabilityStyles: ProductAvailabilityStyles = {
    container: {
        gap: 0,
        css: css`
            flex-grow: 0;
            // margin-bottom:.3rem;
        `,
    },
    messageGridItem: { width: 12 },
    availabilityByWarehouseLinkGridItem: { width: 12 },
    availabilityByWarehouseModal: {
        size: 400,
        cssOverrides: {
            modalContent: css`
                padding: 0;
            `,
            modalTitle: css`
                padding: .625rem .625rem .625rem 1.25rem
            `,
            headlineTypography: css`
                font-size: 1.25rem;
                margin-bottom: 0;
            `,
        },
    },
    centeringWrapper: {
        css: css`
            height: 6.25rem;
            width: 100%;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: 0 auto;
        `,
    },
    messageText: {
        css: css`
            width: 100%;
            ${wrapInContainerStyles}
        `,
    },
    realTimeText: {
        weight: "bold",
        css: css`
            display: inline-block;
            text-align: left;
            min-width: 1.5625rem;

            &::after {
                overflow: hidden;
                display: inline-block;
                vertical-align: bottom;
                animation: ellipsis steps(4, end) 900ms infinite 1s;
                content: "\\2026";
                width: 0;
            }

            @keyframes ellipsis {
                to {
                    width: 1.25em;
                }
            }
        `,
    },
    errorText: {
        weight: "bold",
    },
    labelStyled: {
        css: css`
        font-weight: 600 !important;
        font-size: .9rem !important;
        margin-bottom: .2em;
        font-family: 'Lato',sans-serif;
        margin-right: .5rem;
        `,
    },
    quantityStyled: {
        css: css`
        font-weight: 400 !important;
        `,
    },
    requestTime: {
        css: css`
        span {
            font-size: .9rem;
            color:#333F48 !important;
            text-decoration: underline;
            font-weight: 700 !important;
            &:hover {
                color:#2060AD !important;
            }
        }
        `,
    },
    messageTextNew: {
        css: css`
         &.availability-msg {
            display: flex;
         }
        `,
    },
    messageTextContainer: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    stockContainerStyle: {
        css: css`
        display: flex;
        margin-bottom: 0.6rem;
        `,
    },
    outStockContainerStyle: {
        css: css`
            display: flex;
            margin-bottom: 0.4rem;
        `,
    }

};

const InventoryMessage = styled.div`
    display: flex;
    font-weight: 700;
    font-size: 1rem !important;
    margin-top: 0.75rem;
    font-family: 'Lato',sans-serif;
    > span:first-child {
        font-weight: 700 !important;
    }
    > span:last-child  {
        color: #333F48;
        font-family: 'Lato',sans-serif;
        font-weight: 400;
        font-size: .9rem !important;
        word-break: normal;
    }

    .mb-10 {
        margin-bottom: .31250625rem;
    }

    @media screen and (max-width: 1399px) {
        line-height: 1;

        .mb-10 {
            margin-bottom: .625rem;
        }
    }

    &.ship-manufacture-align {
        display: block;
    }

    .req-lead-time {
        margin-top: 10px;
    }

    .availability-msg {
        display: block;
        margin-top: 10px;
    }

    ${IconWrapper} {
        margin-right: .625rem;
    }
    ${({ theme }: { theme: BaseTheme }) =>
        breakpointMediaQueries(
            theme,
            [
                null,
                css`
                    //margin-bottom: .31250625rem;
                `,
                null,
                null,
            ],
            "max",
        )};
       
`;

const getAvailabilityColor = (productSettings: ProductSettingsModel, availabilityMessageType?: AvailabilityMessageType) => {
    if (!productSettings.showInventoryAvailability) {
        return "common";
    }

    const availabilityType = availabilityMessageType;
    if (availabilityType === AvailabilityMessageType.OutOfStock) {
        return "danger";
    }

    if (availabilityType === AvailabilityMessageType.LowStock) {
        return "warning";
    }
    if (availabilityType === AvailabilityMessageType.ShipDirecrFromManufacturer) {
        return "danger";
    }

    return "success";
};

const showLink = (
    availability: AvailabilityDto | undefined,
    trackInventory: boolean,
    productSettings: ProductSettingsModel,
    isProductDetailsPage?: boolean,
): boolean => {
    if (
        !availability ||
        availability.messageType === AvailabilityMessageType.NoMessage ||
        !trackInventory ||
        !productSettings.showInventoryAvailability ||
        !productSettings.displayInventoryPerWarehouse
    ) {
        return false;
    }

    if (!productSettings.displayInventoryPerWarehouseOnlyOnProductDetail) {
        return true;
    }

    return !!isProductDetailsPage;
};

const ProductAvailability = ({
    productId,
    availability,
    unitOfMeasure,
    trackInventory,
    productSettings,
    isProductDetailsPage,
    getRealTimeWarehouseInventory,
    failedToLoadInventory,
    extendedStyles,
    manufacturerItem,
    manufacturerName,
    isDirectShipFromManufacturer,
    inventoryOutofStock,
    showForeignStockCountry,
    showVendorStocksSetting,
    domesticStockCountries,
    brand,
    sku
}: Props) => {
    const manufacturerItemData = manufacturerItem || "";
    const manufacturerNameData = manufacturerName || "";
    const isDirectShipFromManufacturerFlag = isDirectShipFromManufacturer ? isDirectShipFromManufacturer.toLowerCase() : "false";
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [warehouses, setWarehouses] = useState<WarehouseDto[] | undefined>(undefined);
    const [errorMessage, setErrorMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };
    const viewAvailabilityByWarehouseClickHandler = () => {
        setIsLoading(true);
        setModalIsOpen(true);
        getRealTimeWarehouseInventory({
            productId,
            unitOfMeasure,
            expand: ["warehouses"],
            onComplete: result => {
                setWarehouses(result.warehouses);
                setErrorMessage(result.errorMessage);
                setIsLoading(false);
            },
        });
    };

    const [isForeignStock, setIsForeignStock] = useState(true);
    const [countriesWithDomesticStock, setCountriesWithDomesticStock] = useState(domesticStockCountries);

    const [foreignStockQty, setForeignStockQty] = useState(0);
    const [domesticStockQty, setDomesticStockQty] = useState(0);
    const [vendorStockQty, setVendorStockQty] = useState(0);

    const [showForeignStock, setShowForeignStock] = useState("");
    const [showVendorStock, setVendorStock] = useState(false);
    

    useEffect(() => {
        getCurrentWebsiteCountry().then(response => {
            if (!countriesWithDomesticStock){
                // fallback to current website country.
                setCountriesWithDomesticStock([response]);
            }
        });
    }, []);

    useEffect(() => {
        getRealTimeWarehouseInventory({
            productId,
            unitOfMeasure,
            expand: ["warehouses"],
            onComplete: result => {
                let domesticStockQty = 0;
                let foreignStockQty = 0;
                let vendorStock = 0;
                result?.warehouses?.forEach(resultData => {
                    if (resultData?.description === "VirtualStock") {
                        vendorStock += resultData?.qty;
                        setVendorStockQty(vendorStock);
                    }
                    else if (countriesWithDomesticStock?.includes(resultData.description)) {
                        setIsForeignStock(false);
                        domesticStockQty += resultData?.qty;
                        setDomesticStockQty(domesticStockQty);
                    }
                    else {
                        foreignStockQty += resultData?.qty;
                        setForeignStockQty(foreignStockQty);
                    }

                });

                if (showForeignStockCountry) {
                    setShowForeignStock("Yes");
                }
                else {
                    setShowForeignStock("No");
                }

                if (showVendorStocksSetting) {
                    setVendorStock(true);
                }
            },
        });
    }, [countriesWithDomesticStock]);

    const color = getAvailabilityColor(productSettings, availability?.messageType || undefined);
    const [styles] = useState(() => mergeToNew(productAvailabilityStyles, extendedStyles));

    if (failedToLoadInventory) {
        return (
            <StyledWrapper {...styles.errorWrapper}>
                <Typography {...styles.errorText}>{siteMessage("RealTimeInventory_InventoryLoadFailed")}</Typography>
            </StyledWrapper>
        );
    }

    let inventoryTextComponent = <Typography {...styles.realTimeText} />;

    if (
        availability?.messageType !== AvailabilityMessageType.NoMessage &&
        availability?.message?.length &&
        isDirectShipFromManufacturerFlag !== "true"
    ) {
        inventoryTextComponent = (
            <InventoryMessage color={color} {...styles.inventoryMessage}>
                <Typography {...styles.messageText} data-test-selector="availabilityMessage" className={`${availability.message === "Request Lead Time" ? "req-lead-time" : "stock-container"}`} >
                  
                    {availability.message.match(/In Stock/gi) && domesticStockQty > 0 && (
                        <StyledWrapper {...styles.stockContainerStyle}>
                            <Typography>
                                <Typography {...styles.labelStyled}>{siteMessage("Inventory_DomesticStockMessage")}:</Typography>
                                <Typography {...styles.quantityStyled}>{domesticStockQty}</Typography>
                            </Typography>
                        </StyledWrapper>
                    )}
                    {availability.message.match(/In Stock/gi) && showForeignStock === "Yes" && foreignStockQty > 0 && (
                        <StyledWrapper {...styles.stockContainerStyle}>
                            <Typography>
                                <Typography {...styles.labelStyled}>{siteMessage("Inventory_ForeignStockMessage")}:</Typography>
                                <Typography {...styles.quantityStyled}>{foreignStockQty}</Typography>
                            </Typography>
                        </StyledWrapper>
                    )}
                    {availability.message.match(/In Stock/gi) && showVendorStock && vendorStockQty > 0 && (
                        <StyledWrapper {...styles.stockContainerStyle}>
                            <Typography>
                                <Typography {...styles.labelStyled}>{siteMessage("Inventory_VendorStockMessage")}:</Typography>
                                <Typography {...styles.quantityStyled}>{vendorStockQty}</Typography>
                            </Typography>
                        </StyledWrapper>
                    )}
                    {availability.message.match(/In Stock/gi) && domesticStockQty <= 0 && foreignStockQty <= 0  && vendorStockQty <= 0 &&
                        ((showForeignStock === "No" && vendorStockQty <= 0) ||
                            (showForeignStock === "Yes" && vendorStockQty <= 0) ||
                            (showForeignStock === "No" && showVendorStock === false && vendorStockQty >= 0) ||
                            (showForeignStock === "Yes" && showVendorStock === false && vendorStockQty >= 0)
                        ) && (

                            <StyledWrapper {...styles.outStockContainerStyle}>
                                <Typography {...styles.labelStyled}>{siteMessage("Inventory_OutofStock")}</Typography>
                            </StyledWrapper>
                        )}
                    {availability.message === "Request Lead Time" && (
                        <Typography>
                            <Link href={`/Request-Lead-Time?mfpn=${manufacturerItemData}&mf=${manufacturerNameData}&brand=${brand}&sku=${sku}`} {...styles.requestTime}>
                                <Typography>{availability.message}</Typography>
                            </Link>
                        </Typography>
                    )}

                    <Typography>
                        {availability.message !== "In Stock" && availability.message !== "Request Lead Time" && !availability.message.match(/In Stock/gi) && (
                            <Typography>{availability.message}</Typography>
                        )}
                    </Typography>
                    {availability.message !== "Request Lead Time" && inventoryOutofStock === "Request Lead Time" && (
                        <Typography as="div" className="request-link mb-10">
                            <Link href={`/Request-Lead-Time?mfpn=${manufacturerItemData}&mf=${manufacturerNameData}&brand=${brand}&sku=${sku}`} {...styles.requestTime}>
                                <Typography>{inventoryOutofStock}</Typography>
                            </Link>
                        </Typography>
                    )}
                </Typography>
            </InventoryMessage>
        );
    } else if (isDirectShipFromManufacturerFlag === "true") {
        inventoryTextComponent = (
            <InventoryMessage
                color={getAvailabilityColor(productSettings, AvailabilityMessageType.ShipDirecrFromManufacturer)}
                {...styles.messageTextContainer}
                className="ship-manufacture-align"
            >
                {vendorStockQty > 0 && (
                    <StyledWrapper {...styles.stockContainerStyle}>
                        <Typography>
                            <Typography {...styles.quantityStyled}>{vendorStockQty}:</Typography>
                            <Typography {...styles.labelStyled}>{siteMessage("Inventory_VendorStockMessage")}</Typography>
                        </Typography>
                    </StyledWrapper>
                )}
                {availability?.message === "Request Lead Time" && (
                    <Typography>
                        <Link href={`/Request-Lead-Time?mfpn=${manufacturerItemData}&mf=${manufacturerNameData}&brand=${brand}&sku=${sku}`} {...styles.requestTime}>
                            <Typography>{availability?.message}</Typography>
                        </Link>
                    </Typography>
                )}
                {availability?.message !== "Request Lead Time" && inventoryOutofStock === "Request Lead Time" && (
                    <Typography as="div" className="request-link mb-10">
                        <Link href={`/Request-Lead-Time?mfpn=${manufacturerItemData}&mf=${manufacturerNameData}&brand=${brand}&sku=${sku}`} {...styles.requestTime}>
                            <Typography>{inventoryOutofStock}</Typography>
                        </Link>
                    </Typography>
                )}
                <Typography {...styles.messageTextNew} data-test-selector="availabilityMessage" className="availability-msg">
                    {siteMessage("Inventory_ShipDirectFromManufacturer")}
                </Typography>

            </InventoryMessage>
        );

    } else if (availability) {
        inventoryTextComponent = (
            <InventoryMessage
                color={getAvailabilityColor(productSettings, AvailabilityMessageType.ShipDirecrFromManufacturer)}
                {...styles.inventoryMessage}
                className="ship-manufacture-align"
            >
                {inventoryOutofStock === "Request Lead Time" && (
                    <Typography as="div" className="request-link mb-10">
                        <Link href={`/Request-Lead-Time?mfpn=${manufacturerItemData}&mf=${manufacturerNameData}&brand=${brand}&sku=${sku}`} {...styles.requestTime}>
                            <Typography>{inventoryOutofStock}</Typography>
                        </Link>
                    </Typography>
                )}
                <Typography {...styles.messageTextNew} data-test-selector="availabilityMessage">
                    {siteMessage("Inventory_ShipDirectFromManufacturer")}
                </Typography>

            </InventoryMessage>
        );
    }

    return (
        <GridContainer {...styles.container} className="adjust-spacing">
            <GridItem {...styles.messageGridItem}>{inventoryTextComponent}</GridItem>
            {showLink(availability, trackInventory, productSettings, isProductDetailsPage) && (
                <GridItem {...styles.availabilityByWarehouseLinkGridItem}>
                    <Link
                        {...styles.availabilityByWarehouseLink}
                        onClick={viewAvailabilityByWarehouseClickHandler}
                        data-test-selector="availabilityByWarehouseLink"
                    >
                        {translate("View Availability by Warehouse")}
                    </Link>
                    <Modal
                        {...styles.availabilityByWarehouseModal}
                        headline={translate("Warehouse Inventory")}
                        isOpen={modalIsOpen}
                        handleClose={modalCloseHandler}
                    >
                        {isLoading && (
                            <StyledWrapper {...styles.centeringWrapper}>
                                <LoadingSpinner {...styles.spinner} />
                            </StyledWrapper>
                        )}
                        {!isLoading && (
                            <>
                                {errorMessage ? (
                                    <div>{errorMessage}</div>
                                ) : (
                                    <DataTable
                                        {...styles.availabilityByWarehouseTable}
                                        data-test-selector="availabilityByWarehouseModal"
                                    >
                                        <DataTableHead {...styles.availabilityByWarehouseTableHead}>
                                            <DataTableHeader {...styles.availabilityByWarehouseTableHeader}>
                                                {translate("Warehouse")}
                                            </DataTableHeader>
                                            <DataTableHeader {...styles.availabilityByWarehouseTableHeader}>
                                                {translate("QTY")}
                                            </DataTableHeader>
                                        </DataTableHead>
                                        <DataTableBody {...styles.availabilityByWarehouseTableBody}>
                                            {warehouses?.map(warehouse => (
                                                <DataTableRow
                                                    {...styles.availabilityByWarehouseTableRow}
                                                    key={warehouse.name}
                                                >
                                                    <DataTableCell {...styles.availabilityByWarehouseTableCell}>
                                                        {warehouse.description || warehouse.name}
                                                    </DataTableCell>
                                                    <DataTableCell {...styles.availabilityByWarehouseTableCell}>
                                                        {warehouse.qty}
                                                    </DataTableCell>
                                                </DataTableRow>
                                            ))}
                                        </DataTableBody>
                                    </DataTable>
                                )}
                            </>
                        )}
                    </Modal>
                </GridItem>
            )}
        </GridContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductAvailability);
